// i18n strings for the IVR Flow Builder feature (src/features/ivr).
// Single file, both locales, merged under the `ivr.*` namespace by
// src/i18n/en.js and src/i18n/ar.js (mirrors campaigns.js).
export default {
  en: {
    nodes: {
      audio: "Audio",
      language: "Language",
      time: "Time",
      queue: "Queue",
      trunk: "Trunk",
      api: "API",
      condition: "Condition",
    },
    handles: {
      next: "Next",
      match: "Match",
      nomatch: "No match",
      success: "Success",
      failure: "Failure",
      else: "Otherwise",
    },
    fields: {
      node_name: "Node name",
      node_name_ph: "e.g. welcome_audio",
      is_root: "Entry point of the flow",
      select_ph: "Select…",
      any: "Any",
      audio_file: "Audio file",
      allow_interrupt: "Allow user interruption",
      menu_options: "DTMF menu options (optional)",
      digit: "Digit",
      option_label: "Label (optional)",
      add_option: "Add option",
      language_digits: "Key per language",
      no_languages: "No languages configured on the PBX.",
      queue: "Queue",
      trunk: "Trunk",
      destination: "Destination number",
      time_rule: "Time rule",
      time_range: "Time range",
      days_of_week: "Days of week",
      days_of_month: "Days of month",
      months: "Months",
      api: "API request",
      url: "URL",
      method: "Method",
      response_variable: "Response variable",
      params: "Parameters",
      param_key: "Key",
      param_value: "Value",
      add_param: "Add parameter",
      condition_var: "Variable to check",
      condition_value: "Matches value",
    },
    builder: {
      palette: "Nodes",
      hide: "Hide panel",
      palette_hint: "Drag onto the canvas, or double-click.",
      empty_canvas: "Drag a node here to start building your flow",
      not_configured: "Not configured",
      delete_node: "Delete node",
      entry: "Entry",
      zoom_in: "Zoom in",
      zoom_out: "Zoom out",
      fit: "Fit to view",
      auto_layout: "Auto-arrange",
      back: "Back to flows",
      untitled: "Untitled flow",
      new_name: "New IVR flow",
      editing: "Editing flow",
      new: "New flow",
      tree_settings: "Flow settings",
      save: "Save",
      ok: "OK",
    },
    inspector: {
      empty: "Select a node to configure it",
    },
    sim: {
      title: "Simulator",
      simulate: "Simulate",
      stop: "Stop",
      restart: "Restart",
      path: "Path so far",
      continue: "Continue",
      unwired: "not connected",
      play: "Play",
      stop_audio: "Stop",
      playing: "Plays audio: {file}",
      language_pick: "Caller picks a language:",
      no_input: "No input (timeout)",
      invalid_key: "Invalid key",
      time_prompt: "Evaluate the time rule using…",
      use_system_time: "Current system time",
      use_custom_time: "A time I pick",
      custom_time: "Pick a date & time",
      evaluate: "Evaluate",
      api_prompt: "Calls the API and stores the result in {var}.",
      api_value: "Mock response value for {var}",
      api_value_ph: "e.g. active",
      api_success: "API call succeeded",
      api_failure: "API call failed",
      cond_auto: "{var} = “{val}” → takes the “{branch}” branch",
      cond_pick: "Does {var} match “{val}”?",
      end_flow: "End of flow — call ends.",
      end_no_nodes: "Nothing to simulate — the flow has no nodes.",
      end_dead_end: "Dead end — this branch isn't connected to a node.",
      end_transfer: "Transferred out of the IVR.",
      end_hangup: "Call ended (hang up).",
      end_max_retries: "Maximum retries reached — call ends.",
      end_queue: "Placed into queue “{queue}”.",
      end_trunk: "Dialled out to {dest}.",
    },
    settings: {
      title: "Flow settings",
      name: "Flow name",
      name_ph: "Enter a name for this flow",
      short_code: "Short code",
      invalid: "Invalid input",
      max_retries: "Max retries",
      audio_filename: "Audio file",
      final_audio: "Final audio",
      timeout: "Timeout",
      timeout_seconds: "Timeout (seconds)",
      timeout_retries: "Timeout retries",
      done: "Done",
    },
    validation: {
      title: "Please fix the following",
      name_required: "Give the flow a name (in Flow settings).",
      short_code_required: "Set a short code (in Flow settings).",
      no_nodes: "Add at least one node to the canvas.",
      dangling_edge: "A connection points to a node that no longer exists.",
      node_name_required: "Every node needs a name.",
      node_name_duplicate: "More than one node is named “{name}”.",
      audio_file: "“{node}” is missing its audio file.",
      language: "“{node}” has no key assigned to any language.",
      time_range: "“{node}” needs a start and end time.",
      queue: "“{node}” has no queue selected.",
      trunk: "“{node}” has no trunk selected.",
      api_url: "“{node}” is missing its URL.",
      condition: "“{node}” has no variable to check.",
      orphans: "{count} nodes are not reachable from the entry node.",
      ambiguous_audio_language:
        "{names} exist in more than one language and this flow has no Language node, so the server decides which is played. Add a Language node, or upload the file under a language-specific name.",
      dropped_edges:
        "{count} saved connections were dropped on load: a node can have only one parent, and a Trunk or Queue node has no outgoing branch. Saving makes this permanent.",
    },
    messages: {
      saved: "IVR flow saved.",
      no_menu_id: "The flow was created but the server didn't return its id — reopen the list and edit it to continue.",
      no_node_id: "The server didn't return an id for node “{node}”, so connections could not be saved.",
    },
    list: {
      title: "IVR Flows",
      subtitle: "Build and manage visual IVR call flows.",
      search: "Search flows…",
      refresh: "Refresh",
      new: "New flow",
      empty: "No IVR flows yet — create your first one.",
      col_name: "Name",
      col_short_code: "Short code",
      col_status: "Status",
      col_actions: "Actions",
      enabled: "Enabled",
      disabled: "Disabled",
      enable: "Enable",
      disable: "Disable",
      edit: "Edit",
      sounds: "Sounds",
    },
    sounds: {
      title: "IVR Sounds",
      subtitle: "Audio files available to every IVR flow.",
      back: "Back to flows",
      search: "Search sounds…",
      refresh: "Refresh",
      empty: "No sounds uploaded yet.",
      col_name: "File name",
      col_language: "Language",
      col_actions: "Actions",
      play: "Play",
      stop: "Stop",
      play_failed: "Couldn't play this file.",
      delete: "Delete",
      delete_title: "Delete sound?",
      delete_text:
        "“{name}” will be removed. Any IVR flow that plays it will have no audio for that step.",
      delete_confirm: "Delete",
      deleted: "Sound deleted.",
      upload_btn: "Upload",
      upload_title: "Upload a sound",
      file_label: "Audio file",
      file_hint: "WAV format.",
      name_label: "Stored as",
      name_ph: "e.g. welcome_audio",
      name_hint: "The name flows refer to — no extension.",
      name_invalid: "Use 2–100 letters, digits, dot, dash or underscore.",
      language_label: "Language",
      cancel: "Cancel",
      retry: "Retry",
      uploaded: "Sound uploaded.",
    },
  },
  ar: {
    nodes: {
      audio: "تشغيل صوت",
      language: "تحديد اللغة",
      time: "التوقيت",
      queue: "قائمة انتظار",
      trunk: "خط خارجي",
      api: "طلب API",
      condition: "شرط",
    },
    handles: {
      next: "التالي",
      match: "مطابق",
      nomatch: "غير مطابق",
      success: "نجاح",
      failure: "فشل",
      else: "غير ذلك",
    },
    fields: {
      node_name: "اسم العنصر",
      node_name_ph: "مثال: welcome_audio",
      is_root: "نقطة بداية المسار",
      select_ph: "اختر…",
      any: "الكل",
      audio_file: "الملف الصوتي",
      allow_interrupt: "السماح للمتصل بمقاطعة التشغيل",
      menu_options: "خيارات قائمة DTMF (اختيارية)",
      digit: "الزر",
      option_label: "الوصف (اختياري)",
      add_option: "إضافة خيار",
      language_digits: "الزر الخاص بكل لغة",
      no_languages: "لا توجد لغات مُعرّفة على المقسم.",
      queue: "قائمة الانتظار",
      trunk: "الخط الخارجي",
      destination: "رقم الوجهة",
      time_rule: "قاعدة التوقيت",
      time_range: "النطاق الزمني",
      days_of_week: "أيام الأسبوع",
      days_of_month: "أيام الشهر",
      months: "الأشهر",
      api: "طلب API",
      url: "الرابط (URL)",
      method: "طريقة الطلب",
      response_variable: "متغيّر حفظ الاستجابة",
      params: "المعاملات",
      param_key: "المفتاح",
      param_value: "القيمة",
      add_param: "إضافة معامل",
      condition_var: "المتغيّر المطلوب فحصه",
      condition_value: "القيمة المطابقة",
    },
    builder: {
      palette: "العناصر",
      hide: "إخفاء اللوحة",
      palette_hint: "اسحب عنصراً إلى مساحة العمل، أو انقر عليه نقراً مزدوجاً.",
      empty_canvas: "اسحب عنصراً إلى هنا لتبدأ ببناء المسار",
      not_configured: "لم تُضبط بعد",
      delete_node: "حذف العنصر",
      entry: "البداية",
      zoom_in: "تكبير",
      zoom_out: "تصغير",
      fit: "ملاءمة العرض",
      auto_layout: "ترتيب تلقائي",
      back: "الرجوع إلى المسارات",
      untitled: "مسار بلا اسم",
      new_name: "مسار رد صوتي جديد",
      editing: "تعديل المسار",
      new: "مسار جديد",
      tree_settings: "إعدادات المسار",
      save: "حفظ",
      ok: "حسناً",
    },
    inspector: {
      empty: "اختر عنصراً لضبط إعداداته",
    },
    sim: {
      title: "المحاكاة",
      simulate: "محاكاة",
      stop: "إيقاف",
      restart: "إعادة التشغيل",
      path: "المسار حتى الآن",
      continue: "متابعة",
      unwired: "غير موصول",
      play: "تشغيل",
      stop_audio: "إيقاف",
      playing: "يشغّل الصوت: {file}",
      language_pick: "يختار المتصل اللغة:",
      no_input: "لا يوجد إدخال (انتهت المهلة)",
      invalid_key: "زر غير صالح",
      time_prompt: "قيّم قاعدة التوقيت باستخدام…",
      use_system_time: "توقيت النظام الحالي",
      use_custom_time: "توقيت أُحدّده",
      custom_time: "اختر التاريخ والوقت",
      evaluate: "تقييم",
      api_prompt: "يستدعي الـ API ويحفظ النتيجة في {var}.",
      api_value: "قيمة استجابة تجريبية لـ {var}",
      api_value_ph: "مثال: active",
      api_success: "نجح استدعاء الـ API",
      api_failure: "فشل استدعاء الـ API",
      cond_auto: "{var} = ”{val}“ ← يسلك الفرع ”{branch}“",
      cond_pick: "هل يطابق {var} القيمة ”{val}“؟",
      end_flow: "نهاية المسار — يُنهى الاتصال.",
      end_no_nodes: "لا شيء لمحاكاته — المسار لا يحتوي على عناصر.",
      end_dead_end: "طريق مسدود — هذا الفرع غير موصول بأي عنصر.",
      end_transfer: "تم التحويل خارج الرد الصوتي.",
      end_hangup: "انتهى الاتصال (إنهاء المكالمة).",
      end_max_retries: "تم بلوغ الحد الأقصى للمحاولات — يُنهى الاتصال.",
      end_queue: "تمت الإضافة إلى قائمة الانتظار ”{queue}“.",
      end_trunk: "تم الاتصال الخارجي بـ {dest}.",
    },
    settings: {
      title: "إعدادات المسار",
      name: "اسم المسار",
      name_ph: "أدخل اسماً لهذا المسار",
      short_code: "الرمز المختصر",
      invalid: "عند إدخال غير صحيح",
      max_retries: "أقصى عدد للمحاولات",
      audio_filename: "الملف الصوتي",
      final_audio: "الملف الصوتي الأخير",
      timeout: "عند انتهاء المهلة",
      timeout_seconds: "المهلة (بالثواني)",
      timeout_retries: "عدد محاولات المهلة",
      done: "تم",
    },
    validation: {
      title: "يرجى معالجة ما يلي",
      name_required: "أدخل اسماً للمسار من إعدادات المسار.",
      short_code_required: "حدّد الرمز المختصر من إعدادات المسار.",
      no_nodes: "أضف عنصراً واحداً على الأقل إلى مساحة العمل.",
      dangling_edge: "يوجد اتصال يشير إلى عنصر لم يعد موجوداً.",
      node_name_required: "كل عنصر يحتاج إلى اسم.",
      node_name_duplicate: "يوجد أكثر من عنصر بالاسم ”{name}“.",
      audio_file: "العنصر ”{node}“ لم يُحدَّد له ملف صوتي.",
      language: "العنصر ”{node}“ لم يُحدَّد له زر لأي لغة.",
      time_range: "العنصر ”{node}“ يحتاج إلى وقت بداية ونهاية.",
      queue: "العنصر ”{node}“ لم تُحدَّد له قائمة انتظار.",
      trunk: "العنصر ”{node}“ لم يُحدَّد له خط خارجي.",
      api_url: "العنصر ”{node}“ لم يُحدَّد له رابط.",
      condition: "العنصر ”{node}“ لم يُحدَّد له متغيّر للفحص.",
      orphans: "يوجد {count} عناصر لا يمكن الوصول إليها من نقطة البداية.",
      ambiguous_audio_language:
        "الملفات {names} موجودة بأكثر من لغة وهذا المسار لا يحتوي على عنصر لغة، لذا يحدّد الخادم أيها يُشغّل. أضف عنصر لغة، أو ارفع الملف باسم خاص بلغة معيّنة.",
      dropped_edges:
        "تم تجاهل {count} من الاتصالات المحفوظة عند التحميل: لا يمكن أن يكون للعنصر أكثر من مصدر واحد، وعنصر الخط الخارجي أو قائمة الانتظار ليس له مخرج. الحفظ سيجعل هذا نهائياً.",
    },
    messages: {
      saved: "تم حفظ المسار بنجاح.",
      no_menu_id: "تم إنشاء المسار لكن الخادم لم يُرجع معرّفه — افتح القائمة وعدّله للمتابعة.",
      no_node_id: "لم يُرجع الخادم معرّفاً للعنصر ”{node}“، لذا لم يتم حفظ الاتصالات.",
    },
    list: {
      title: "مسارات الرد الصوتي",
      subtitle: "أنشئ مسارات الرد الصوتي التفاعلي وأدِرها بصرياً.",
      search: "ابحث في المسارات…",
      refresh: "تحديث",
      new: "مسار جديد",
      empty: "لا توجد مسارات بعد — ابدأ بإنشاء أول مسار.",
      col_name: "الاسم",
      col_short_code: "الرمز المختصر",
      col_status: "الحالة",
      col_actions: "الإجراءات",
      enabled: "مُفعّل",
      disabled: "مُعطّل",
      enable: "تفعيل",
      disable: "تعطيل",
      edit: "تعديل",
      sounds: "الملفات الصوتية",
    },
    sounds: {
      title: "الملفات الصوتية للرد الصوتي",
      subtitle: "الملفات الصوتية المتاحة لجميع مسارات الرد الصوتي.",
      back: "الرجوع إلى المسارات",
      search: "ابحث في الملفات…",
      refresh: "تحديث",
      empty: "لم يتم رفع أي ملف صوتي بعد.",
      col_name: "اسم الملف",
      col_language: "اللغة",
      col_actions: "الإجراءات",
      play: "تشغيل",
      stop: "إيقاف",
      play_failed: "تعذّر تشغيل هذا الملف.",
      delete: "حذف",
      delete_title: "حذف الملف الصوتي؟",
      delete_text:
        "سيتم حذف ”{name}“. أي مسار رد صوتي يشغّله لن يجد ملفاً صوتياً لتلك الخطوة.",
      delete_confirm: "حذف",
      deleted: "تم حذف الملف الصوتي.",
      upload_btn: "رفع",
      upload_title: "رفع ملف صوتي",
      file_label: "الملف الصوتي",
      file_hint: "بصيغة WAV.",
      name_label: "يُحفظ باسم",
      name_ph: "مثال: welcome_audio",
      name_hint: "الاسم الذي تشير إليه المسارات — بدون امتداد.",
      name_invalid: "استخدم من 2 إلى 100 حرفاً أو رقماً أو نقطة أو شرطة أو شرطة سفلية.",
      language_label: "اللغة",
      cancel: "إلغاء",
      retry: "إعادة المحاولة",
      uploaded: "تم رفع الملف الصوتي.",
    },
  },
};
